import { Component, Input, OnInit, inject } from '@angular/core';
import { Device } from '@capacitor/device';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HttpErrorCodes } from 'src/app/core/constants/error.constants';
import { FormFieldName, FormType, FormValidatorTypeKeys } from 'src/app/core/constants/form.constants';
import { UserEventAction } from 'src/app/core/constants/user-event.constants';
import { IText } from 'src/app/core/models/content.model';
import { FormRegex, FormValidatorType } from 'src/app/core/models/form-validator.model';
import { ITextControl } from 'src/app/core/models/form.model';
import { IHttpError } from 'src/app/core/models/http.model';
import { ReCaptchaAction } from 'src/app/core/models/reCaptcha.request';
import { ReCaptchaService } from 'src/app/core/services/re-captcha.service';
import { UserService } from 'src/app/core/services/user.service';
import {
  EmailResetCodePhoneValidationRequest,
  EmailResetCodeRequest,
  EmailResetCodeValidationRequest
} from 'src/app/features/content-blocks/email-reset/email-reset.model';
import { BaseFormComponent } from 'src/app/features/form/base-form.component';
import { ModalService } from 'src/app/features/modal/modal.service';

import { FormComponent } from '../../../features/form/form.component';
import { ButtonLoaderDirective } from '../../directives/button-loader.directive';
import { InternalInnerHtmlDirective } from '../../directives/internal-inner-html.directive';
import { IEmailResetModal } from './email-reset-modal.model';

@Component({
  selector: 'air-email-reset-modal',
  templateUrl: './email-reset-modal.component.html',
  imports: [InternalInnerHtmlDirective, FormComponent, ButtonLoaderDirective, TranslatePipe]
})
export class EmailResetModalComponent extends BaseFormComponent<EmailResetCodePhoneValidationRequest> implements OnInit {
  protected reCaptchaService = inject(ReCaptchaService);
  private modalService = inject(ModalService);
  private translateService = inject(TranslateService);
  private userService = inject(UserService);

  @Input() content: IEmailResetModal;

  isResendLoading: boolean;
  isResendDisabled: boolean;
  isLoading: boolean;

  protected phoneNumber: string;
  protected code: string;
  protected selectedViewState: 'phoneNumber' | 'emailResetCode' = 'phoneNumber';
  protected readonly alternateReCaptchaActions = [ReCaptchaAction.sendEmailResetCode, ReCaptchaAction.resetEmailCodeValidation];

  get text(): IText | undefined {
    if (this.selectedViewState === 'phoneNumber') {
      return this.content?.emailResetText;
    } else {
      const emailResetCodeText = Object.assign({}, this.content?.emailResetCodeText);
      emailResetCodeText.body = emailResetCodeText.body.format([this.phoneNumber]);
      return emailResetCodeText;
    }
  }

  get enableAlternateReCaptcha(): boolean {
    return this.enableSendEmailResetCodeAlternateReCaptcha || this.enableResetEmailCodeValidationAlternateReCaptcha;
  }

  get enableSendEmailResetCodeAlternateReCaptcha(): boolean {
    return this.reCaptchaService?.enableAlternateReCaptcha?.[ReCaptchaAction.sendEmailResetCode] ?? false;
  }

  get enableResetEmailCodeValidationAlternateReCaptcha(): boolean {
    return this.reCaptchaService?.enableAlternateReCaptcha?.[ReCaptchaAction.resetEmailCodeValidation] ?? false;
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.form = [
      {
        controls: [
          {
            type: FormType.phoneNumber,
            name: FormFieldName.phoneNumber,
            label: this.translateService.instant('account.label.phone_number') as string,
            autocomplete: 'tel',
            placeholder: this.content?.maskedPhoneNumber,
            validators: [
              {
                validator: FormValidatorType.required,
                errorMessage: this.translateService.instant('account.error.phone_number.required') as string
              },
              {
                validator: FormValidatorType.pattern(FormRegex.phoneNumber.getRegExp()),
                errorMessage: this.translateService.instant('account.error.phone_number.invalid') as string
              }
            ],
            hidden: this.selectedViewState !== 'phoneNumber'
          } as ITextControl,
          {
            type: FormType.numberOnly,
            name: FormFieldName.emailResetCode,
            label: this.translateService.instant('account.label.validation_code') as string,
            validators: [
              {
                validator: FormValidatorType.required,
                errorMessage: this.translateService.instant('account.error.validation_code.required') as string
              },
              {
                validator: FormValidatorType.pattern(FormRegex.authenticationCode.getRegExp()),
                errorMessage: this.translateService.instant('account.error.validation_code.invalid') as string
              },
              {
                validator: FormValidatorType.verificationCode,
                errorMessage: this.translateService.instant('account.error.validation_code.incorrect') as string
              }
            ],
            hidden: this.selectedViewState !== 'emailResetCode'
          } as ITextControl
        ]
      }
    ];

    if (this.content.resendButtonDisabledTimeout && this.content.resendButtonDisabledTimeout > 0) {
      this.isResendDisabled = true;

      setTimeout(() => {
        this.isResendDisabled = false;
      }, this.content.resendButtonDisabledTimeout);
    }

    this.userEventSource = `${this.content?.userEventSource} - email reset`;
  }

  async submitValues(values: EmailResetCodePhoneValidationRequest): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    switch (this.selectedViewState) {
      case 'phoneNumber':
        void this.sendCode(values.phoneNumber)
          .then(() => this.goToValidationStep())
          .finally(() => {
            this.isSubmitting = false;
          });

        this.disableResend();
        break;
      case 'emailResetCode':
        void this.validateCode(values.code).finally(() => {
          this.isSubmitting = false;
        });
        break;
      default:
        break;
    }
  }

  resendCode(event: Event): void {
    this.pushUserEvent(UserEventAction.submit, event.text());

    this.isSubmitting = true;
    void this.sendCode(this.phoneNumber).finally(() => {
      this.isSubmitting = false;
    });

    this.disableResend();
  }

  submitAlternateReCaptcha(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.enableSendEmailResetCodeAlternateReCaptcha) {
      this.isSubmitting = true;
      const phoneNumber = this.formComponent.form.get<string>(FormFieldName.phoneNumber)?.value;
      void this.sendCode(phoneNumber)
        .then(() => this.goToValidationStep())
        .finally(() => (this.isSubmitting = false));

      this.disableResend();
      return;
    }

    if (this.enableResetEmailCodeValidationAlternateReCaptcha) {
      this.isSubmitting = true;
      const emailResetCode = this.formComponent.form.get<string>(FormFieldName.emailResetCode)?.value;
      void this.validateCode(emailResetCode).finally(() => (this.isSubmitting = false));
      return;
    }
  }

  onCloseModal(checkCloseCallback = true, pushUserEvent = true): void {
    if (pushUserEvent) {
      this.pushUserEvent(UserEventAction.close);
    }

    this.modalService.close(checkCloseCallback);
  }

  private disableResend(): void {
    this.isResendLoading = true;
    setTimeout(() => {
      this.isResendLoading = false;
    }, 30000);
  }

  private async sendCode(phoneNumber: string): Promise<void> {
    this.phoneNumber = phoneNumber;

    await this.reCaptchaService
      .verify<
        void,
        EmailResetCodeRequest
      >(ReCaptchaAction.sendEmailResetCode, new EmailResetCodeRequest(this.content.cardNumber, phoneNumber, (await Device.getId()).identifier), this.userService.sendEmailResetCode.bind(this.userService))
      .catch((error: IHttpError) => {
        if ([HttpErrorCodes.invalidPhoneNumber].includes(error?.error?.code)) {
          this.formComponent.form.get(FormFieldName.phoneNumber)?.setErrors({ [FormValidatorTypeKeys.pattern]: true });
        }

        this.formComponent.reCaptchaFallbackComponent?.reset();
        throw error;
      });
  }

  private async validateCode(code: string): Promise<void> {
    this.code = code;
    await this.reCaptchaService
      .verify<void, EmailResetCodeValidationRequest>(
        ReCaptchaAction.resetEmailCodeValidation,
        new EmailResetCodeValidationRequest(code),
        this.userService.resetEmailCodeValidation.bind(this.userService)
      )
      .then(async () => {
        this.onCloseModal(false, false);

        if (this.content.verifyCallback) {
          await this.content.verifyCallback(this.phoneNumber, code);
        }
      })
      .catch((error: IHttpError) => {
        if ([HttpErrorCodes.forbidden, HttpErrorCodes.invalidEmailResetCode].includes(error?.error?.code)) {
          this.formComponent.form.get(FormFieldName.emailResetCode)?.setErrors({ [FormValidatorTypeKeys.verificationCode]: true });
        }

        this.formComponent.reCaptchaFallbackComponent?.reset();
        throw error;
      });
  }

  private goToValidationStep(): void {
    this.selectedViewState = 'emailResetCode';
    this.formComponent.formState.submitted = false;

    this.form = this.form.map((section) => ({
      ...section,
      controls: section.controls.map((control) => {
        if (control.name === FormFieldName.phoneNumber) {
          return { ...control, hidden: true };
        }

        if (control.name === FormFieldName.emailResetCode) {
          return { ...control, hidden: false };
        }

        return control;
      })
    }));
  }
}
